import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import {
  subscribeToClients,
  createClient as createClientService,
  updateClient as updateClientService,
  deleteClient as deleteClientService,
} from '@/lib/projects';
import type { Client } from '@/types';

export const useClients = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // クライアント一覧の購読
    const unsubscribe = subscribeToClients(user.uid, (data: Client[]) => {
      setClients(data);
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [user]);

  const createClient = async (clientData: Omit<Client, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!user || isProcessing) return;

    setIsProcessing(true);
    try {
      await createClientService(user.uid, clientData);
    } finally {
      setIsProcessing(false);
    }
  };

  const updateClient = async (clientId: string, clientData: Partial<Client>) => {
    if (!user || isProcessing) return;

    setIsProcessing(true);
    try {
      await updateClientService(user.uid, clientId, clientData);
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteClient = async (clientId: string) => {
    if (!user || isProcessing) return;

    setIsProcessing(true);
    try {
      await deleteClientService(user.uid, clientId);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    clients,
    isLoading,
    isProcessing,
    createClient,
    updateClient,
    deleteClient,
  };
};
